import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';
import FloatingSymbols from '../components/FloatingSymbols';
import BrandLogo from '../components/BrandLogo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4 overflow-hidden">
      <FloatingSymbols opacity={0.9} />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <BrandLogo size={44} className="mb-2 justify-center w-full" />
          <p className="text-slate-600">Sign in to your agent account</p>
          <p className="text-slate-600">Insurance CRM for Agents & IMFs</p>
        </div>
        
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>Sign In</CardTitle>
            <CardDescription>Enter your credentials to access your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" data-testid="login-email-label">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="agent@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="border-slate-300 focus:border-blue-800 focus:ring-blue-800"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" data-testid="login-password-label">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="border-slate-300 focus:border-blue-800 focus:ring-blue-800"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-blue-800 hover:bg-blue-900 text-white font-medium transition-colors duration-200"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="spinner mr-2" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <LogIn className="mr-2 h-4 w-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
            
            <div className="mt-4 text-center text-sm">
              <span className="text-slate-600">Don't have an account? </span>
              <Link to="/register" className="text-blue-800 hover:text-blue-900 font-medium" data-testid="register-link">
                Sign up
              </Link>
            </div>
            
            <div className="mt-2 text-center text-sm">
              <Link to="/forgot-password" className="text-slate-600 hover:text-blue-800" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
